import { useEffect, useRef, useState } from "react";
import { apiGet, type Appointment } from "../api/client";

declare global {
  interface Window {
    jQuery?: JQueryStatic;
    moment?: unknown;
  }
}

type JQueryStatic = ((sel: string | Element) => {
  fullCalendar: (opts?: unknown) => unknown;
  empty: () => unknown;
}) & {
  fn?: { fullCalendar?: unknown };
};

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = false;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(s);
  });
}

function loadCss(href: string) {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const l = document.createElement("link");
  l.rel = "stylesheet";
  l.href = href;
  document.head.appendChild(l);
}

/**
 * Map synthetic appointment → FullCalendar event, matching Angular
 * `eventFromAppt` + `colorizeEvent` in public/master/js/modules/ctrl/appts.js.
 */
function eventFromAppt(appt: Appointment) {
  const patientName =
    appt.patient_name || appt.patient?.name || "Patient";
  const cancelled =
    appt.cancelled === true ||
    appt.status === "cancelled" ||
    appt.status === "patient_missed";
  const missed =
    appt.patient_missed === true || appt.status === "patient_missed";

  let className = "";
  let title = patientName;

  if (appt.confirmed === false) {
    className = "fc-event-office-unconfirmed";
  } else if (appt.confirmed === true) {
    className = "fc-event-office-confirmed";
  }

  if (cancelled && !missed) {
    title = `Cancelled - ${patientName}`;
    className = "fc-event-cancelled";
  }
  if (missed) {
    title = `No-Show - ${patientName}`;
    className = "fc-event-no-show";
  }

  // foreign_id_type: "nex" drives the week-view "N" badge via historical CSS
  if (appt.foreign_id_type === "nex" || appt.foreign_id_type == null) {
    className = `${className} fc-event-booked-via-nexhealth`.trim();
  }

  const start = appt.start_time || appt.start_at;
  const end =
    appt.end_time ||
    appt.end_at ||
    (start
      ? new Date(
          new Date(start).getTime() +
            (typeof appt.appointment_type === "object" &&
            appt.appointment_type?.minutes
              ? appt.appointment_type.minutes
              : 60) *
              60_000,
        ).toISOString()
      : undefined);

  return {
    id: appt.id,
    title,
    start,
    end,
    allDay: false,
    stick: true,
    confirmed: appt.confirmed,
    className,
  };
}

/**
 * Angular parity: FullCalendar week view (same vendored 2.x stack as the
 * historical SPA: moment + jQuery + fullcalendar) with uiConfig from appts.js.
 */
export function CalendarPage() {
  const elRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [includeCancelled, setIncludeCancelled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        loadCss("/vendor/fullcalendar.min.css");
        await loadScript("/vendor/jquery.min.js");
        await loadScript("/vendor/moment.min.js");
        await loadScript("/vendor/fullcalendar.min.js");
        if (cancelled) return;
        setReady(true);
      } catch (e) {
        if (!cancelled) setError(String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ready || !elRef.current) return;
    const $ = window.jQuery;
    if (!$?.fn?.fullCalendar) {
      setError("FullCalendar failed to initialize");
      return;
    }

    let destroyed = false;
    const node = elRef.current;

    apiGet<Appointment[]>("/appointments").then((data) => {
      if (destroyed || !node) return;
      const all = Array.isArray(data) ? data : [];
      // Live Angular archive paints cancelled + overnight events (server
      // ignores cancelled=false; FC defaults are 00:00–24:00). Match that.
      const events = all.map(eventFromAppt);
      const $el = $(node);
      try {
        $el.fullCalendar("destroy");
      } catch {
        /* first mount */
      }
      $el.empty();
      $el.fullCalendar({
        // Match public/master/js/modules/ctrl/appts.js uiConfig.calendar
        defaultView: "agendaWeek",
        editable: false,
        nowIndicator: true,
        header: {
          left: "month,agendaWeek,agendaDay",
          center: "prev,today,next",
          right: "title",
        },
        // FullCalendar 2.x defaults are lowercase — Angular does not override.
        buttonText: {
          today: "today",
          month: "month",
          week: "week",
          day: "day",
        },
        buttonIcons: {
          prev: " ne__icon__arrow--left",
          next: " ne__icon__arrow--right",
        },
        eventOverlap: false,
        timezone: "local",
        eventLimit: 8,
        eventLimitText: "more...",
        allDaySlot: false,
        slotLabelFormat: "h(:mm) A",
        displayEventTime: true,
        displayEventEnd: false,
        slotDuration: "00:15:00",
        slotLabelInterval: "00:60:00",
        // No minTime/maxTime — Angular uses FC defaults (00:00–24:00).
        // Angular: height = window - appointmentsWrapper.top - 32
        height: Math.max(780, window.innerHeight - 80),
        // scrollTime positions the agenda at 6 AM. Do NOT force scrollTop=0
        // afterward — that undoes scrollTime and leaves midnight at the top.
        scrollTime: "06:00:00",
        events,
      });
    });

    return () => {
      destroyed = true;
      try {
        $(node).fullCalendar("destroy");
      } catch {
        /* noop */
      }
    };
  }, [ready, includeCancelled]);

  return (
    <div className="ne__appointments ne__calendar ne__calendar--fullcalendar">
      {/* Historical Angular: uib-alert without close handler → close hidden; arrow icon after copy */}
      <div
        className="ne__alert ne__alert--full-width ne__alert--calendar alert"
        role="alert"
      >
        <span>Please choose a provider from the Providers dropdown.</span>
        <i className="ne__icon__arrow-stick-down--thick" aria-hidden="true" />
      </div>
      {error ? (
        <div className="ne__empty-message">
          <p>{error}</p>
        </div>
      ) : null}
      {!ready && !error ? (
        <div className="ne__empty-message">
          <p>Loading calendar…</p>
        </div>
      ) : null}
      <div className="calendar-app">
        <div className="ne__calendar__toolbar">
          <button
            type="button"
            className="ne__btn ne__btn--tertiary ne__btn--md dropdown-toggle ne__calendar__filter"
            onClick={() => setFilterOpen((v) => !v)}
          >
            Filter
          </button>
          {filterOpen ? (
            <div className="ne__calendar__filter-panel" role="dialog">
              <label className="ne__calendar__filter-option">
                <input
                  type="checkbox"
                  checked={includeCancelled}
                  onChange={(e) => setIncludeCancelled(e.target.checked)}
                />
                Include cancelled appointments
              </label>
            </div>
          ) : null}
        </div>
        <div ref={elRef} className="ne__fullcalendar-host" />
      </div>
    </div>
  );
}
